/**
 * Camelot wheel — DJ-friendly key compatibility.
 *
 * Each musical key maps to a Camelot code like "8A" (A minor) or "8B"
 * (C major). Compatible mixes follow simple rules:
 *
 *   - Same code        → identical key (perfect)
 *   - ±1 on the number → adjacent key, neighbour mix
 *   - Same number, swap A↔B → relative major/minor (energy boost)
 *
 * https://mixedinkey.com/harmonic-mixing-guide/
 */

export type CamelotCode = string;

const KEY_TO_CAMELOT: Record<string, CamelotCode> = {
  'A minor': '8A', 'Am': '8A',
  'E minor': '9A', 'Em': '9A',
  'B minor': '10A', 'Bm': '10A',
  'F# minor': '11A', 'F#m': '11A', 'Gb minor': '11A',
  'C# minor': '12A', 'C#m': '12A', 'Db minor': '12A',
  'G# minor': '1A', 'G#m': '1A', 'Ab minor': '1A',
  'D# minor': '2A', 'D#m': '2A', 'Eb minor': '2A',
  'A# minor': '3A', 'A#m': '3A', 'Bb minor': '3A',
  'F minor': '4A', 'Fm': '4A',
  'C minor': '5A', 'Cm': '5A',
  'G minor': '6A', 'Gm': '6A',
  'D minor': '7A', 'Dm': '7A',
  'C major': '8B', 'C': '8B',
  'G major': '9B', 'G': '9B',
  'D major': '10B', 'D': '10B',
  'A major': '11B', 'A': '11B',
  'E major': '12B', 'E': '12B',
  'B major': '1B', 'B': '1B',
  'F# major': '2B', 'F#': '2B', 'Gb major': '2B', 'Gb': '2B',
  'C# major': '3B', 'C#': '3B', 'Db major': '3B', 'Db': '3B',
  'G# major': '4B', 'G#': '4B', 'Ab major': '4B', 'Ab': '4B',
  'D# major': '5B', 'D#': '5B', 'Eb major': '5B', 'Eb': '5B',
  'A# major': '6B', 'A#': '6B', 'Bb major': '6B', 'Bb': '6B',
  'F major': '7B', 'F': '7B',
};

export function toCamelot(key: string | undefined | null): CamelotCode | null {
  if (!key) return null;
  const trimmed = key.trim();
  if (KEY_TO_CAMELOT[trimmed]) return KEY_TO_CAMELOT[trimmed];
  const norm = trimmed.replace(/\bmaj\b/i, 'major').replace(/\bmin\b/i, 'minor').trim();
  if (KEY_TO_CAMELOT[norm]) return KEY_TO_CAMELOT[norm];
  const lower = norm.toLowerCase();
  for (const [k, v] of Object.entries(KEY_TO_CAMELOT)) {
    if (k.toLowerCase() === lower) return v;
  }
  return null;
}

interface CamelotParts { number: number; letter: 'A' | 'B'; }

function parseCamelot(code: CamelotCode): CamelotParts | null {
  const m = /^(\d{1,2})([AB])$/i.exec(code);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (n < 1 || n > 12) return null;
  return { number: n, letter: m[2].toUpperCase() as 'A' | 'B' };
}

/**
 * Score (0..1) how compatible two Camelot codes are. 1 = same key, 0.9 =
 * relative major/minor, 0.85 = ±1 neighbour, 0.55 = ±2 same letter,
 * 0.45 = ±1 with letter swap, 0 otherwise.
 */
export function camelotCompatibility(a: CamelotCode | null, b: CamelotCode | null): number {
  if (!a || !b) return 0;
  const pa = parseCamelot(a);
  const pb = parseCamelot(b);
  if (!pa || !pb) return 0;
  if (pa.number === pb.number && pa.letter === pb.letter) return 1.0;
  if (pa.number === pb.number) return 0.9;
  const diff = Math.min(
    Math.abs(pa.number - pb.number),
    12 - Math.abs(pa.number - pb.number),
  );
  if (pa.letter === pb.letter) {
    if (diff === 1) return 0.85;
    if (diff === 2) return 0.55;
  }
  if (diff === 1) return 0.45;
  return 0.0;
}

/**
 * BPM proximity score (0..1). DJs accept ±6% pitch; we score linearly to
 * 0.6 inside that window and decay to zero between 6% and 12% apart.
 */
export function bpmCompatibility(a: number | undefined | null, b: number | undefined | null): number {
  if (!a || !b) return 0;
  const ratio = Math.abs(a - b) / Math.max(a, b);
  if (ratio === 0) return 1;
  if (ratio < 0.06) return 1 - (ratio / 0.06) * 0.4;
  if (ratio < 0.12) return 0.6 - ((ratio - 0.06) / 0.06) * 0.6;
  return 0;
}

export interface MixCompatibility {
  score: number;
  key: number;
  bpm: number;
  camelotA: CamelotCode | null;
  camelotB: CamelotCode | null;
}

export function mixCompatibility(opts: {
  keyA?: string | null;
  keyB?: string | null;
  bpmA?: number | null;
  bpmB?: number | null;
}): MixCompatibility {
  const camelotA = toCamelot(opts.keyA ?? null);
  const camelotB = toCamelot(opts.keyB ?? null);
  const key = camelotCompatibility(camelotA, camelotB);
  const bpm = bpmCompatibility(opts.bpmA ?? null, opts.bpmB ?? null);
  return {
    score: key * 0.6 + bpm * 0.4,
    key,
    bpm,
    camelotA,
    camelotB,
  };
}
